'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  getWishlist,
  toggleWishlist,
  WISHLIST_CHANGED,
  type StoredProduct,
} from '@/lib/wishlist';
import { addLine } from '@/lib/cart-store';
import { useWishlistDrawer, closeWishlistDrawer } from '@/lib/wishlist-drawer';
import { openCartDrawer, closeCartDrawer } from '@/lib/cart-drawer';

// Cached snapshot of the wishlist. useSyncExternalStore requires getSnapshot
// to return the SAME reference when nothing changed; getWishlist() reads
// localStorage and JSON.parses fresh on every call, which would hand React a
// new array reference each render → 'Maximum update depth' (React error #185)
// → page collapses into the global error boundary.
//
// Equally important: subscribe / getSnapshot / getServerSnapshot must be
// reference-stable across renders. Inline arrow functions create a new
// reference on every render, which causes useSyncExternalStore to re-subscribe
// on every render and is itself enough to trigger the loop in some setups.
// Hoist all three to module scope.
let snapshot: StoredProduct[] = [];
let snapshotInited = false;
const EMPTY_SNAPSHOT: StoredProduct[] = [];

function refreshSnapshot() {
  snapshot = getWishlist();
}

function subscribe(notify: () => void): () => void {
  // Lazy-init on first subscribe so SSR doesn't read window.localStorage.
  if (!snapshotInited) {
    refreshSnapshot();
    snapshotInited = true;
  }
  function handler() {
    refreshSnapshot();
    notify();
  }
  window.addEventListener(WISHLIST_CHANGED, handler);
  return () => window.removeEventListener(WISHLIST_CHANGED, handler);
}

function getSnapshot(): StoredProduct[] {
  return snapshot;
}

function getServerSnapshot(): StoredProduct[] {
  return EMPTY_SNAPSHOT;
}

function useWishlistItems(): StoredProduct[] {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function WishlistDrawer() {
  const open  = useWishlistDrawer();
  const items = useWishlistItems();
  const [pending, setPending] = useState<string | null>(null);
  const router = useRouter();

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeWishlistDrawer();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function handleRemove(item: StoredProduct) {
    toggleWishlist(item);
  }

  async function handleMoveToBag(item: StoredProduct) {
    // Only single-size, buyable products carry a variant id. Anything else
    // (multi-size, or an older saved entry) goes to its page to pick a size.
    if (!item.variantId) {
      closeWishlistDrawer();
      router.push(`/shop/${item.id}`);
      return;
    }
    setPending(item.id);
    try {
      const cents = Math.round(parseFloat(item.price.replace(/[^0-9.]/g, '')) * 100) || 0;
      const res = addLine(
        {
          variantId: item.variantId, productId: item.id, slug: item.id, name: item.name,
          variantTitle: null, image: item.image, priceCents: cents, maxQty: 10, preorder: false,
        },
        1,
      );
      if (res.ok) {
        // Take it out of the wishlist once it is in the bag.
        toggleWishlist(item);
        closeWishlistDrawer();
        openCartDrawer();
      }
    } finally {
      setPending(null);
    }
  }

  // Keep cart and wishlist drawers mutually exclusive when opened from header
  // icons. A user clicking the heart while the cart is open expects the cart
  // to step aside.
  useEffect(() => {
    if (open) closeCartDrawer();
  }, [open]);

  return (
    <>
      <div
        onClick={closeWishlistDrawer}
        aria-hidden={!open}
        className={[
          'fixed inset-0 bg-ink/40 z-[60]',
          'motion-safe:transition-opacity motion-safe:duration-200',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        ].join(' ')}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Favorites"
        aria-hidden={!open}
        className={[
          'fixed top-0 right-0 h-full w-full sm:w-[420px] bg-bone z-[70]',
          'flex flex-col',
          'motion-safe:transition-[transform,visibility] motion-safe:duration-300',
          // Shadow only while open, and hidden once parked off-screen (see CartDrawer).
          open ? 'translate-x-0 shadow-2xl visible' : 'translate-x-full invisible',
        ].join(' ')}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-ink/10">
          <p className="text-xs uppercase tracking-[0.14em] text-ink/65">Favorites</p>
          <button
            onClick={closeWishlistDrawer}
            aria-label="Close favorites"
            className="text-ink/60 hover:text-ink transition-colors p-1 -m-1"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M6 6L18 18M6 18L18 6" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" className="mx-auto text-ink/40 mb-5">
                <path d="M12 21s-7-4.5-9.5-9C1.2 9.4 2.4 6.5 5 5.4c2-.8 4.2 0 5.5 1.6l.5.6.5-.6c1.3-1.6 3.5-2.4 5.5-1.6 2.6 1.1 3.8 4 2.5 6.6C19 16.5 12 21 12 21z" />
              </svg>
              <p className="font-display tracking-[-0.01em] font-semibold text-xl text-ink mb-3">No favorites yet</p>
              <p className="text-sm text-muted-strong mb-8 leading-relaxed">Tap the heart on any fragrance to save it here.</p>
              <Link
                href="/shop"
                onClick={closeWishlistDrawer}
                className="inline-block bg-plum text-white px-8 py-3 text-xs uppercase tracking-[0.12em] font-medium hover:bg-plum-deep transition-colors rounded-sm"
              >
                Browse the Collection
              </Link>
            </div>
          ) : (
            <ul className="space-y-5">
              {items.map((item) => (
                <li key={item.id} className="grid grid-cols-[64px_1fr_auto] gap-4 items-start">
                  <Link
                    href={`/shop/${item.id}`}
                    onClick={closeWishlistDrawer}
                    className="block aspect-square bg-white overflow-hidden"
                  >
                    {item.image && (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={item.image} alt="" width={64} height={64} loading="lazy" className="w-full h-full object-contain p-[6%]" />
                    )}
                  </Link>
                  <div className="min-w-0">
                    <Link
                      href={`/shop/${item.id}`}
                      onClick={closeWishlistDrawer}
                      className="font-display tracking-[0.06em] font-semibold text-xs text-ink leading-tight block truncate hover:text-gold transition-colors"
                    >
                      {item.name}
                    </Link>
                    <p className="text-xs text-ink font-numerals mt-1">{item.price}</p>
                    <div className="flex gap-3 mt-2">
                      <button
                        onClick={() => handleMoveToBag(item)}
                        disabled={pending === item.id}
                        className="text-xs uppercase tracking-[0.12em] text-ink/80 hover:text-ink transition-colors disabled:opacity-50"
                      >
                        {pending === item.id ? 'Adding…' : item.variantId ? 'Move to bag' : 'Choose size'}
                      </button>
                      <button
                        onClick={() => handleRemove(item)}
                        className="text-xs uppercase tracking-[0.12em] text-muted-strong hover:text-ink transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-ink/10 px-6 py-5 bg-bone">
            <Link
              href="/wishlist"
              onClick={closeWishlistDrawer}
              className="block text-center text-xs uppercase tracking-[0.12em] text-ink/60 hover:text-ink transition-colors"
            >
              View full favorites page
            </Link>
          </div>
        )}
      </aside>
    </>
  );
}
